import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import jsPDF from 'jspdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail } from 'lucide-react';

import { useToast } from '@/components/ui/use-toast';

const schema = z.object({
  coName: z.string().min(1, 'Company name is required'),
  coWebsite: z.string().optional(),
  entityType2: z.string().min(1, 'Entity type is required'),
  regNo2: z.string().min(1, 'Registration number is required'),
  kraPin2: z.string().min(1, 'KRA PIN is required'),
  tccExpiry2: z.string().min(1, 'TCC expiry date is required'),
  contactName2: z.string().min(1, 'Contact name is required'),
  contactEmail2: z.string().email('Valid email is required'),
  contactPhone2: z.string().min(1, 'Contact phone is required'),
  address2: z.string().optional(),
  categories: z.array(z.string()).min(1, 'At least one category must be selected'),
  boDisclosure2: z.string().min(1, 'Beneficial ownership disclosure is required'),
  bankName: z.string().min(1, 'Bank name is required'),
  bankBranch: z.string().min(1, 'Bank branch is required'),
  acctName: z.string().min(1, 'Account name is required'),
  acctNo: z.string().min(1, 'Account number is required'),
  swift: z.string().optional(),
  mpesaPaybill: z.string().optional(),
  clients: z.string().optional(),
  signName2: z.string().min(1, 'Signatory name is required'),
  signTitle2: z.string().min(1, 'Signatory title is required'),
  ethics2: z.boolean().refine(val => val === true, 'Ethics declaration is required'),
  hse: z.boolean().optional(),
  dpConsent2: z.boolean().refine(val => val === true, 'Data protection consent is required'),
});

type FormData = z.infer<typeof schema>;

interface SupplierRegistrationFormProps {
  onClose: () => void;
}

const SupplierRegistrationForm: React.FC<SupplierRegistrationFormProps> = ({ onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const { toast } = useToast();
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const categoryOptions = [
    { value: 'ICT-equipment', label: 'ICT Equipment & Peripherals' },
    { value: 'office-stationery', label: 'Office Stationery & Supplies' },
    { value: 'furniture-fixtures', label: 'Furniture & Fixtures' },
    { value: 'printing-branding', label: 'Printing & Branding' },
    { value: 'security-surveillance', label: 'Security & Surveillance' },
    { value: 'consultancy', label: 'Consultancy/Professional Services' },
    { value: 'transport-logistics', label: 'Transport & Logistics' },
    { value: 'utilities-maintenance', label: 'Building Maintenance & Utilities' },
    { value: 'other', label: 'Other' },
  ];

  const handleCategoryChange = (category: string, checked: boolean) => {
    let newCategories;
    if (checked) {
      newCategories = [...selectedCategories, category];
    } else {
      newCategories = selectedCategories.filter(c => c !== category);
    }
    setSelectedCategories(newCategories);
    setValue('categories', newCategories);
  };

  const generatePDF = async (data: FormData) => {
    const pdf = new jsPDF();
    
    // Header with SACCO branding
    pdf.setFontSize(16);
    pdf.setTextColor(10, 111, 78); // Primary green color
    pdf.text('NATCON STAFF SACCO', 20, 20);
    
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('KCB Towers, Kenya Road', 140, 15);
    pdf.text('P.O. Box 21046-00100', 140, 20);
    pdf.text('Tel: +254111485951', 140, 25);
    pdf.text('Email: natconsacco@nca.go.ke', 140, 30);
    
    // Title
    pdf.setFontSize(14);
    pdf.setTextColor(10, 111, 78);
    pdf.text('SUPPLIER REGISTRATION FORM', 20, 45);
    
    let yPosition = 60;
    
    // Form content
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    
    const fields = [
      { label: 'Company Name:', value: data.coName },
      { label: 'Website:', value: data.coWebsite || 'N/A' },
      { label: 'Entity Type:', value: data.entityType2 },
      { label: 'Registration No.:', value: data.regNo2 },
      { label: 'KRA PIN:', value: data.kraPin2 },
      { label: 'TCC Expiry:', value: data.tccExpiry2 },
      { label: 'Contact Person:', value: data.contactName2 },
      { label: 'Contact Email:', value: data.contactEmail2 },
      { label: 'Contact Phone:', value: data.contactPhone2 },
      { label: 'Address:', value: data.address2 || 'N/A' },
      { label: 'Bank Name:', value: data.bankName },
      { label: 'Bank Branch:', value: data.bankBranch },
      { label: 'Account Name:', value: data.acctName },
      { label: 'Account Number:', value: data.acctNo },
      { label: 'SWIFT/BIC:', value: data.swift || 'N/A' },
      { label: 'M-Pesa Paybill:', value: data.mpesaPaybill || 'N/A' },
      { label: 'Authorized Signatory:', value: data.signName2 },
      { label: 'Designation:', value: data.signTitle2 },
    ];
    
    fields.forEach((field) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.setFont(undefined, 'bold');
      pdf.text(field.label, 20, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(field.value, 80, yPosition);
      yPosition += 8;
    });
    
    // Categories
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }
    pdf.setFont(undefined, 'bold');
    pdf.text('Supply Categories:', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    const categoriesText = data.categories.map(cat => 
      categoryOptions.find(opt => opt.value === cat)?.label || cat
    ).join(', ');
    const categoryLines = pdf.splitTextToSize(categoriesText, 170);
    pdf.text(categoryLines, 20, yPosition);
    yPosition += categoryLines.length * 5 + 5;
    
    // Beneficial ownership (multiline)
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }
    pdf.setFont(undefined, 'bold');
    pdf.text('Beneficial Ownership:', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    const boLines = pdf.splitTextToSize(data.boDisclosure2, 170);
    pdf.text(boLines, 20, yPosition);
    yPosition += boLines.length * 5 + 5;
    
    // Client references
    if (data.clients) {
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.setFont(undefined, 'bold');
      pdf.text('Client References:', 20, yPosition);
      yPosition += 8;
      pdf.setFont(undefined, 'normal');
      const clientLines = pdf.splitTextToSize(data.clients, 170);
      pdf.text(clientLines, 20, yPosition);
    }
    
    return pdf;
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Generate PDF
      const pdf = await generatePDF(data);
      const pdfBlob = pdf.output('blob');
      const filename = `supplier_registration_${data.coName.replace(/\s+/g, '_')}.pdf`;

      // Prepare email content
      const subjectText = 'Supplier Registration Application';
      const bodyText = `Dear Natcon Staff Sacco Procurement Team,

I am writing to submit our supplier registration application to be included in your approved supplier database.

Company Details:
Company Name: ${data.coName}
Contact Person: ${data.contactName2}
Email: ${data.contactEmail2}
Phone: ${data.contactPhone2}
KRA PIN: ${data.kraPin2}

Supply Categories:
${data.categories.join('\n')}

Please find the completed supplier registration form attached. We will submit all required documents as requested.

We are committed to maintaining high standards of service and compliance with all regulatory requirements.

Thank you for considering our application.

Best regards,
${data.signName2}
${data.signTitle2}
${data.coName}`;

      // Check if Web Share API is available and can share files
      if ('share' in navigator && 'canShare' in navigator) {
        const file = new File([pdfBlob], filename, { type: 'application/pdf' });
        
        // Check if the browser can share files
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: subjectText,
              text: `To: natconsacco@nca.go.ke\nSubject: ${subjectText}\n\n${bodyText}`,
            });
            
            toast({ 
              title: 'Email client opened', 
              description: 'PDF attached successfully. Complete sending the email from your email app.' 
            });
            onClose();
            return;
          } catch (error: any) {
            // User cancelled or share failed
            if (error.name !== 'AbortError') {
              console.warn('Share failed:', error);
            }
          }
        }
      }

      // Fallback: Download PDF and open email with instructions
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Create email with pre-filled content
      const subject = encodeURIComponent(subjectText);
      const body = encodeURIComponent(bodyText);
      const mailtoLink = `mailto:natconsacco@nca.go.ke?subject=${subject}&body=${body}`;
      
      // Wait a moment for download to start, then open email
      setTimeout(() => {
        window.location.href = mailtoLink;
        
        toast({ 
          title: 'Next steps', 
          description: `1. PDF downloaded as "${filename}"\n2. Email client opening\n3. Attach the PDF file and send`,
          duration: 10000
        });
      }, 1000);

      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({ 
        variant: 'destructive', 
        title: 'Submission failed', 
        description: 'Please try again or contact support.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader className="bg-gradient-primary text-primary-foreground">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">Supplier Registration Form</CardTitle>
            <p className="text-primary-foreground/90 text-sm mt-1">
              Apply to be listed in Natcon SACCO's supplier register for future RFQs/tenders.
            </p>
          </div>
          <div className="text-right text-sm">
            <p>KCB Towers, Kenya Road</p>
            <p>P.O. Box 21046-00100</p>
            <p>Tel: +254111485951</p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Details */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Company Details</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="coName">Legal Entity Name *</Label>
                <Input id="coName" {...register('coName')} />
                {errors.coName && <p className="text-destructive text-sm">{errors.coName.message}</p>}
              </div>
              <div>
                <Label htmlFor="coWebsite">Website (optional)</Label>
                <Input id="coWebsite" type="url" {...register('coWebsite')} placeholder="https://example.com" />
              </div>
              <div>
                <Label htmlFor="entityType2">Entity Type *</Label>
                <Select onValueChange={(value) => setValue('entityType2', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="sole-proprietor">Sole Proprietor</SelectItem>
                    <SelectItem value="cooperative">Cooperative/SACCO</SelectItem>
                    <SelectItem value="ngo">NGO/Nonprofit</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.entityType2 && <p className="text-destructive text-sm">{errors.entityType2.message}</p>}
              </div>
              <div>
                <Label htmlFor="regNo2">Registration/Inc. No. *</Label>
                <Input id="regNo2" {...register('regNo2')} />
                {errors.regNo2 && <p className="text-destructive text-sm">{errors.regNo2.message}</p>}
              </div>
              <div>
                <Label htmlFor="kraPin2">KRA PIN *</Label>
                <Input id="kraPin2" {...register('kraPin2')} placeholder="A123456789B" />
                {errors.kraPin2 && <p className="text-destructive text-sm">{errors.kraPin2.message}</p>}
              </div>
              <div>
                <Label htmlFor="tccExpiry2">TCC Expiry Date *</Label>
                <Input id="tccExpiry2" type="date" {...register('tccExpiry2')} />
                {errors.tccExpiry2 && <p className="text-destructive text-sm">{errors.tccExpiry2.message}</p>}
              </div>
              <div>
                <Label htmlFor="contactName2">Primary Contact Person *</Label>
                <Input id="contactName2" {...register('contactName2')} />
                {errors.contactName2 && <p className="text-destructive text-sm">{errors.contactName2.message}</p>}
              </div>
              <div>
                <Label htmlFor="contactEmail2">Contact Email *</Label>
                <Input id="contactEmail2" type="email" {...register('contactEmail2')} />
                {errors.contactEmail2 && <p className="text-destructive text-sm">{errors.contactEmail2.message}</p>}
              </div>
              <div>
                <Label htmlFor="contactPhone2">Contact Phone *</Label>
                <Input id="contactPhone2" {...register('contactPhone2')} placeholder="+2547XXXXXXXX" />
                {errors.contactPhone2 && <p className="text-destructive text-sm">{errors.contactPhone2.message}</p>}
              </div>
              <div>
                <Label htmlFor="address2">Physical/Postal Address</Label>
                <Input id="address2" {...register('address2')} />
              </div>
            </div>
          </fieldset>

          {/* Supply Categories */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Supply Categories</legend>
            <div className="mt-4">
              <Label>Select categories you wish to supply *</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {categoryOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <Checkbox 
                      id={option.value}
                      checked={selectedCategories.includes(option.value)}
                      onCheckedChange={(checked) => handleCategoryChange(option.value, checked === true)}
                    />
                    <Label htmlFor={option.value} className="text-sm">{option.label}</Label>
                  </div>
                ))}
              </div>
              {errors.categories && <p className="text-destructive text-sm">{errors.categories.message}</p>}
            </div>
          </fieldset>

          {/* Beneficial Ownership */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Beneficial Ownership</legend>
            <div className="mt-4">
              <Label htmlFor="boDisclosure2">Beneficial Owners Summary *</Label>
              <Textarea 
                id="boDisclosure2" 
                {...register('boDisclosure2')} 
                placeholder="List full names, ID/passport, nationality, % ownership/control for each BO"
                className="min-h-24"
              />
              {errors.boDisclosure2 && <p className="text-destructive text-sm">{errors.boDisclosure2.message}</p>}
            </div>
          </fieldset>

          {/* Banking & References */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Banking & References</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="bankName">Bank Name *</Label>
                <Input id="bankName" {...register('bankName')} />
                {errors.bankName && <p className="text-destructive text-sm">{errors.bankName.message}</p>}
              </div>
              <div>
                <Label htmlFor="bankBranch">Bank Branch *</Label>
                <Input id="bankBranch" {...register('bankBranch')} />
                {errors.bankBranch && <p className="text-destructive text-sm">{errors.bankBranch.message}</p>}
              </div>
              <div>
                <Label htmlFor="acctName">Account Name *</Label>
                <Input id="acctName" {...register('acctName')} />
                {errors.acctName && <p className="text-destructive text-sm">{errors.acctName.message}</p>}
              </div>
              <div>
                <Label htmlFor="acctNo">Account Number *</Label>
                <Input id="acctNo" {...register('acctNo')} />
                {errors.acctNo && <p className="text-destructive text-sm">{errors.acctNo.message}</p>}
              </div>
              <div>
                <Label htmlFor="swift">SWIFT/BIC (if applicable)</Label>
                <Input id="swift" {...register('swift')} />
              </div>
              <div>
                <Label htmlFor="mpesaPaybill">M‑Pesa Paybill/Till (optional)</Label>
                <Input id="mpesaPaybill" {...register('mpesaPaybill')} />
              </div>
            </div>
            <div className="mt-4">
              <Label htmlFor="clients">Recent Clients/References (3)</Label>
              <Textarea 
                id="clients" 
                {...register('clients')} 
                placeholder="1) Org, Contact, Email/Phone; 2) …; 3) …"
                className="min-h-20"
              />
            </div>
          </fieldset>

          {/* Declarations */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Declarations & Compliance</legend>
            <div className="space-y-4 mt-4">
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="ethics2" 
                  onCheckedChange={(checked) => setValue('ethics2', checked === true)}
                />
                <Label htmlFor="ethics2" className="text-sm leading-5">
                  We commit to the Code of Ethics for persons participating in public procurement and asset disposal, 
                  and confirm we are not debarred by PPRA.
                </Label>
              </div>
              {errors.ethics2 && <p className="text-destructive text-sm">{errors.ethics2.message}</p>}
              
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="hse" 
                  onCheckedChange={(checked) => setValue('hse', checked === true)}
                />
                <Label htmlFor="hse" className="text-sm leading-5">
                  We maintain relevant HSE/quality certifications where applicable (e.g., for works or specialized services).
                </Label>
              </div>
              
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="dpConsent2" 
                  onCheckedChange={(checked) => setValue('dpConsent2', checked === true)}
                />
                <Label htmlFor="dpConsent2" className="text-sm leading-5">
                  We consent to Natcon SACCO processing our data for supplier registration, due diligence, 
                  and procurement administration in accordance with the Data Protection Act, 2019.
                </Label>
              </div>
              {errors.dpConsent2 && <p className="text-destructive text-sm">{errors.dpConsent2.message}</p>}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="signName2">Authorized Signatory (Full Name) *</Label>
                  <Input id="signName2" {...register('signName2')} />
                  {errors.signName2 && <p className="text-destructive text-sm">{errors.signName2.message}</p>}
                </div>
                <div>
                  <Label htmlFor="signTitle2">Designation/Title *</Label>
                  <Input id="signTitle2" {...register('signTitle2')} />
                  {errors.signTitle2 && <p className="text-destructive text-sm">{errors.signTitle2.message}</p>}
                </div>
              </div>
            </div>
          </fieldset>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={isSubmitting} className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {isSubmitting ? 'Processing...' : 'Submit Registration'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default SupplierRegistrationForm;